import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Type, Hash, Braces, List, Check } from 'lucide-react';

interface QuickFormatSelectProps {
  value: string;
  onChange: (value: string) => void;
  onAdvancedClick?: () => void;
}

const FORMAT_OPTIONS = [
  {
    value: 'parse_text',
    label: 'String',
    description: 'Pass value as plain text',
    icon: Type,
  },
  {
    value: 'parse_int',
    label: 'Number',
    description: 'Parse value as an integer',
    icon: Hash,
  },
  {
    value: 'parse_json',
    label: 'JSON',
    description: 'Parse value as a JSON object',
    icon: Braces,
  },
  {
    value: 'parse_array',
    label: 'Array',
    description: 'Parse comma-separated values as array',
    icon: List,
  },
] as const;

export function QuickFormatSelect({ value, onChange }: QuickFormatSelectProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const currentOption = FORMAT_OPTIONS.find((o) => o.value === value) || FORMAT_OPTIONS[0];

  // Update dropdown position when opening
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 4,
      left: rect.left,
    });
  }, [open]);

  // Close on outside click — check both trigger and portal dropdown
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-foreground transition-colors bg-muted/70 hover:bg-muted"
      >
        <currentOption.icon className="h-3 w-3 text-muted-foreground" />
        {currentOption.label}
        <ChevronDown className="h-2.5 w-2.5 text-muted-foreground" />
      </button>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed w-56 rounded-lg border border-border bg-background shadow-lg animate-fade-in"
            style={{
              top: dropdownPos.top,
              left: dropdownPos.left,
              zIndex: 9999,
            }}
          >
            {FORMAT_OPTIONS.map((opt) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                    isSelected ? 'bg-primary/5' : 'hover:bg-muted/50'
                  }`}
                >
                  <opt.icon className={`h-3.5 w-3.5 shrink-0 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                  <div className="min-w-0 flex-1">
                    <p className={`text-[11px] font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                      {opt.label}
                    </p>
                    <p className="text-[10px] text-muted-foreground/70">
                      {opt.description}
                    </p>
                  </div>
                  {isSelected && <Check className="h-3 w-3 shrink-0 text-primary" />}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}
